import React, { useEffect, useRef, useState } from "react";
import Plotly from "plotly.js-dist-min";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDDatePicker from "components/MDDatePicker";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavbar from "examples/Navbars/TopNavbar";
import Footer from "layouts/pages/LandingPage/components/Footer";
import { useAuth } from "context/AuthContext";

const timeOptions = [
    { value: "30s", display: "Every 30 Seconds" },
    { value: "10min", display: "Every 10 Minutes" },
    { value: "30min", display: "Every 30 Minutes" },
    { value: "hourly", display: "Hourly" },
    { value: "daily", display: "Daily" },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;

const ChartGenerator: React.FC = () => {
    const plotRef = useRef<HTMLDivElement>(null);
    const { sessionToken } = useAuth();

    // State hooks
    const [locations, setLocations] = useState<
        { id: number; location_name: string; trigger_level: number }[]
    >([]);
    const [locationId, setLocationId] = useState<number | "">("");
    const [startDate, setStartDate] = useState<Date>(
        new Date(Date.now() - 24 * 3600 * 1000)
    );
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [timeIncrement, setTimeIncrement] = useState<string>("hourly");
    const [chartType, setChartType] = useState<"bar" | "scatter">("bar");
    const [dataType, setDataType] = useState<"dBA" | "NC">("dBA");
    const [showThresholdLine, setShowThresholdLine] = useState<boolean>(true);
    const [highlightOverThreshold, setHighlightOverThreshold] = useState<boolean>(true);
    const [thresholdOverride, setThresholdOverride] = useState<string>("");
    const [yRange, setYRange] = useState<[number, number]>([40, 120]);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Derived values
    const timezone = "America/New_York";
    const currentLocation = locations.find((l) => l.id === locationId);
    const threshold = currentLocation?.trigger_level ?? 90;
    const effectiveThreshold =
        thresholdOverride !== "" ? Number(thresholdOverride) : threshold;
    const valueKey = dataType === "NC" ? "nc_value" : "leq_value";

    // Helper: draw or update the chart
    const updateChart = (rawData: any[]) => {

        if (!plotRef.current) {

            return;
        }
        // New: grab and log the container's actual size
        const rect = plotRef.current.getBoundingClientRect();


        if (rawData.length === 0) {
            Plotly.purge(plotRef.current);
            return;
        }

        const container = plotRef.current;

        if (rawData.length === 0) {
            Plotly.purge(container);
            return;
        }

        // Bucket and aggregate
        let times: Date[] = [];
        let vals: number[] = [];
        if (timeIncrement === "10min" || timeIncrement === "30min") {
            const binSize = timeIncrement === "10min" ? 10 * 60e3 : 30 * 60e3;
            const bins: Record<string, number[]> = {};
            rawData.forEach((d) => {
                const t = new Date(d.time).getTime();
                const key = new Date(Math.floor(t / binSize) * binSize).toISOString();
                (bins[key] = bins[key] || []).push(d[valueKey]);
            });
            Object.entries(bins)
                .sort(([a], [b]) => +new Date(a) - +new Date(b))
                .forEach(([k, arr]) => {
                    times.push(new Date(k));
                    vals.push(Math.max(...arr));
                });
        } else {
            const agg: Record<string, number[]> = {};
            rawData.forEach((d) => {
                const utc = new Date(d.time);
                const local = new Date(
                    utc.toLocaleString("en-US", { timeZone: timezone })
                );
                const key =
                    timeIncrement === "daily"
                        ? local.toISOString().split("T")[0]
                        : local.toISOString();
                (agg[key] = agg[key] || []).push(d[valueKey]);
            });
            Object.entries(agg)
                .sort(([a], [b]) => +new Date(a) - +new Date(b))
                .forEach(([k, arr]) => {
                    times.push(new Date(k));
                    vals.push(Math.max(...arr));
                });
        }

        // Build traces
        const traces: Plotly.Data[] = [];
        if (chartType === "scatter") {
            traces.push({
                x: times,
                y: vals,
                type: "scatter",
                mode: "lines",
                line: { shape: "hv", color: "gray" },
            });
            if (highlightOverThreshold) {
                traces.push({
                    x: times,
                    y: vals.map((v) => (v > effectiveThreshold ? v : null)),
                    type: "scatter",
                    mode: "lines",
                    line: { shape: "hv", color: "red" },
                    showlegend: false,
                });
            }
        } else {
            traces.push({
                x: times,
                y: vals,
                type: "bar",
                marker: {
                    color: vals.map((v) =>
                        highlightOverThreshold && v > effectiveThreshold ? "red" : "gray"
                    ),
                },
            });
        }

        // Layout
        const layout: Partial<Plotly.Layout> = {
            title: "Sound Levels",
            xaxis: { title: "Time", type: "date" },
            yaxis: { title: "Level (dB)", range: yRange },
            margin: { t: 30, b: 30, l: 30, r: 20 },
            height: 300,
            shapes:
                showThresholdLine && times.length > 0
                    ? [
                        {
                            type: "line",
                            x0: times[0],
                            x1: times[times.length - 1],
                            y0: effectiveThreshold,
                            y1: effectiveThreshold,
                            line: { dash: "dash", width: 2 },
                        },
                    ]
                    : [],
        };

        Plotly.react(container, traces, layout, { responsive: true });
    };

    // 1) Load locations once
    useEffect(() => {
        if (!sessionToken) return;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/db/organization`, {
                    headers: {
                        Authorization: `Bearer ${sessionToken}`,
                        "Content-Type": "application/json",
                    },
                });
                const json = await res.json();
                if (Array.isArray(json) && json.length > 0) {
                    setLocations(
                        json.map((o: any) => ({
                            id: o.id,
                            location_name: o.location_name,
                            trigger_level: o.trigger_level,
                        }))
                    );
                    setLocationId(json[0].id);
                } else {
                    console.error("Unexpected locations response", json);
                }
            } catch (e) {
                console.error("Failed to load locations", e);
            }
        })();
    }, [sessionToken]);

    // 2) Sync thresholdOverride when threshold changes
    useEffect(() => {
        setThresholdOverride(String(threshold));
    }, [threshold]);

    // 3) Fetch data (only sets state)
    useEffect(() => {
        if (!sessionToken || locationId === "" || !startDate || !endDate) return;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const params = new URLSearchParams({
                    start_time: startDate.toISOString(),
                    end_time: endDate.toISOString(),
                    location_id: String(locationId),
                    dataType,
                });
                if (timeIncrement === "hourly" || timeIncrement === "daily") {
                    params.append("time_increment", timeIncrement);
                }
                console.log("[fetchData]", params.toString());

                const res = await fetch(`${API_BASE_URL}/db/levels?${params}`, {
                    headers: { Authorization: `Bearer ${sessionToken}` },
                });
                if (!res.ok) throw new Error(res.statusText);
                const json = await res.json();


                setData(json);
            } catch (err: any) {
                console.error("Data fetch error", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [
        sessionToken,
        locationId,
        startDate,
        endDate,
        dataType,
        timeIncrement,
    ]);

// 4) Whenever loading goes false, OR any chart inputs change, redraw
    useEffect(() => {
        // only draw once the div is rendered AND we have data
        if (!loading && data.length > 0) {
            updateChart(data);
        } else if (!loading && data.length === 0 && plotRef.current) {
            // if you want to clear an empty-data chart
            Plotly.purge(plotRef.current);
        }
    }, [
        loading,             // ← new!
        data,
        chartType,
        timeIncrement,
        showThresholdLine,
        highlightOverThreshold,
        yRange,
        effectiveThreshold,
        valueKey,
    ]);

    // Date picker handler
    const handleDateChange =
        (setter: (d: Date) => void) => (value: string) => {
            const d = new Date(value);
            if (!isNaN(d.getTime())) setter(d);
        };

    return (
        <DashboardLayout>
            <TopNavbar />
            <MDBox mt={4} mx={3}>
                {/* Controls */}
                <Card>
                    <MDBox p={3}>
                        <MDTypography variant="h5" mb={2}>
                            Generate Sound Level Chart
                        </MDTypography>
                        <Grid container spacing={2}>
                            {/* Column 1: Location & Dates */}
                            <Grid item xs={12} md={4}>
                                <MDTypography variant="h6" gutterBottom>
                                    Location
                                </MDTypography>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    size="small"
                                    value={locationId}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setLocationId(v === "" ? "" : Number(v));
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        Select a location
                                    </MenuItem>
                                    {locations.map((loc) => (
                                        <MenuItem key={loc.id} value={loc.id}>
                                            {loc.location_name}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <MDTypography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    Date & Time Options
                                </MDTypography>
                                <MDBox display="flex" flexDirection="column" gap={2}>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setStartDate(
                                                    new Date(startDate.getTime() - 86400000)
                                                )
                                            }
                                        >
                                            <KeyboardArrowLeftIcon fontSize="small" />
                                        </IconButton>
                                        <MDDatePicker
                                            input={{ label: "Start Date" }}
                                            value={startDate.toISOString().slice(0, 10)}
                                            onChange={handleDateChange(setStartDate)}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setStartDate(
                                                    new Date(startDate.getTime() + 86400000)
                                                )
                                            }
                                        >
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        </IconButton>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setEndDate(new Date(endDate.getTime() - 86400000))
                                            }
                                        >
                                            <KeyboardArrowLeftIcon fontSize="small" />
                                        </IconButton>
                                        <MDDatePicker
                                            input={{ label: "End Date" }}
                                            value={endDate.toISOString().slice(0, 10)}
                                            onChange={handleDateChange(setEndDate)}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setEndDate(new Date(endDate.getTime() + 86400000))
                                            }
                                        >
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        </IconButton>
                                    </MDBox>
                                </MDBox>
                            </Grid>

                            {/* Column 2: Chart & Threshold */}
                            <Grid item xs={12} md={4}>
                                <MDTypography variant="h6" gutterBottom>
                                    Chart & Threshold Options
                                </MDTypography>
                                <MDBox display="flex" flexDirection="column" gap={2}>
                                    <MDBox display="flex" alignItems="center" gap={2}>
                                        <MDTypography variant="subtitle2">
                                            Chart Type:
                                        </MDTypography>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={chartType}
                                            onChange={(_, v) => v && setChartType(v)}
                                        >
                                            <ToggleButton value="bar">
                                                <BarChartIcon />
                                            </ToggleButton>
                                            <ToggleButton value="scatter">
                                                <TimelineIcon />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </MDBox>

                                    <MDBox display="flex" alignItems="center" gap={2}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showThresholdLine}
                                                    onChange={(e) =>
                                                        setShowThresholdLine(e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Show Threshold"
                                        />
                                        <TextField
                                            label="Threshold Level"
                                            type="number"
                                            size="small"
                                            value={thresholdOverride}
                                            onChange={(e) =>
                                                setThresholdOverride(e.target.value)
                                            }
                                            sx={{ width: 80 }}
                                        />
                                    </MDBox>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={highlightOverThreshold}
                                                onChange={(e) =>
                                                    setHighlightOverThreshold(e.target.checked)
                                                }
                                            />
                                        }
                                        label="Highlight Above Threshold"
                                    />

                                    <MDTypography variant="subtitle2">
                                        Data Type:
                                    </MDTypography>
                                    <Select
                                        value={dataType}
                                        onChange={(e) =>
                                            setDataType(e.target.value as "dBA" | "NC")
                                        }
                                        size="small"
                                        sx={{ width: 220 }}
                                    >
                                        <MenuItem value="dBA">
                                            Studio Noise Levels (dBA)
                                        </MenuItem>
                                        <MenuItem value="NC">
                                            Tenant Noise Levels (NC)
                                        </MenuItem>
                                    </Select>
                                </MDBox>
                            </Grid>

                            {/* Column 3: Time Resolution */}
                            <Grid item xs={12} md={4}>
                                <MDBox
                                    sx={{
                                        height: 150,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <RadioGroup
                                        value={timeIncrement}
                                        onChange={(e) => setTimeIncrement(e.target.value)}
                                    >
                                        {timeOptions.map((o) => (
                                            <FormControlLabel
                                                key={o.value}
                                                value={o.value}
                                                control={<Radio />}
                                                label={o.display}
                                            />
                                        ))}
                                    </RadioGroup>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* Chart */}
                <Card sx={{ mt: 4 }}>
                    <MDBox p={3}>
                        {loading ? (
                            <MDTypography>Loading…</MDTypography>
                        ) : error ? (
                            <MDTypography color="error">{error}</MDTypography>
                        ) : (
                            <MDBox
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    position: "relative",
                                    height: "300px",
                                }}
                            >
                                {/* Y‑axis controls */}
                                <MDBox
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        height: "300px",
                                        width: 20,
                                        mr: 0,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setYRange([yRange[0], Math.min(yRange[1] + 5, 200)])
                                        }
                                    >
                                        <KeyboardArrowUpIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setYRange([yRange[0], Math.max(yRange[1] - 5, yRange[0])])
                                        }
                                    >
                                        <KeyboardArrowDownIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setYRange([Math.min(yRange[0] + 5, yRange[1]), yRange[1]])
                                        }
                                    >
                                        <KeyboardArrowUpIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setYRange([Math.max(yRange[0] - 5, 0), yRange[1]])
                                        }
                                    >
                                        <KeyboardArrowDownIcon fontSize="small" />
                                    </IconButton>
                                </MDBox>

                                {/* Plot area */}
                                <div ref={plotRef} style={{ flexGrow: 1, height: "300px" }} />
                            </MDBox>
                        )}
                    </MDBox>
                </Card>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
};

export default ChartGenerator;
